import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Fingerprint, KeyRound, MessageSquare, Pencil, Plus, ShieldOff, Trash2 } from 'lucide-react'
import type { ConfirmationResult, RecaptchaVerifier, MultiFactorResolver } from 'firebase/auth'
import Header from '../components/Header'
import { showToast } from '../components/Toast'
import { useI18n } from '../i18n/useI18n'
import { useWebAuthn } from '../hooks/useWebAuthn'
import { api, ApiError, type WebAuthnCredentialItem } from '../services/api'
import PasskeyEnrollmentModal from '../components/PasskeyEnrollmentModal'
import TotpEnrollmentModal from '../components/TotpEnrollmentModal'
import TotpSignInModal from '../components/TotpSignInModal'
import { resetEnrollmentGate } from '../hooks/useEnrollmentGate'
import { auth } from '../lib/firebase'
import { hasTotpFactor, unenrollTotp, reauthenticateGoogle } from '../lib/totpMfa'
import { getDeviceId } from '../lib/deviceId'
import {
  toE164Jp,
  createInvisibleRecaptcha,
  startPhoneLink,
  unlinkPhone,
} from '../lib/phoneRecovery'

/**
 * Passkey 設定画面 (#734).
 *
 * 機能:
 *   - 登録済 credentials 一覧 (= deviceLabel / 登録日時 / 最終使用)
 *   - 「+ 追加登録」ボタン (= 別端末から登録)
 *   - 個別削除 (= 盗難 / 紛失 / 不要)
 *   - Recovery (= 全削除して再登録)
 */
/** Firestore Timestamp の JSON 表現 (= toJSON 経由で come る {_seconds, _nanoseconds}). */
type FirestoreTimestampLike = { _seconds: number; _nanoseconds?: number }

function isFirestoreTimestamp(v: unknown): v is FirestoreTimestampLike {
  return typeof v === 'object' && v !== null && typeof (v as { _seconds?: unknown })._seconds === 'number'
}

export default function PasskeySettings() {
  const navigate = useNavigate()
  const { t, locale } = useI18n()
  const { supported, config, loading: configLoading } = useWebAuthn()
  const [credentials, setCredentials] = useState<WebAuthnCredentialItem[]>([])
  const [loading, setLoading] = useState(true)
  const [enrollOpen, setEnrollOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // #815: WebAuthn 不可層 (#787) の TOTP factor 管理。flag ON のときだけ
  // 評価する (= flag OFF は Firebase MFA を一切触らず byte-for-byte 不変)。
  const [totpEnrolled, setTotpEnrolled] = useState<boolean | null>(null)
  const [totpBusy, setTotpBusy] = useState(false)
  // #1104: 能動的 TOTP 登録 modal (未 enroll 時のみ導線表示)。
  const [totpEnrollOpen, setTotpEnrollOpen] = useState(false)
  // #1107: SMS 復旧用電話番号 (flag ON のときだけセクション表示)。
  const [smsStatus, setSmsStatus] = useState<{
    enabled: boolean
    registered: boolean
    maskedNumber: string | null
  } | null>(null)
  const [smsStep, setSmsStep] = useState<'idle' | 'phone' | 'code'>('idle')
  const [smsPhone, setSmsPhone] = useState('')
  const [smsCode, setSmsCode] = useState('')
  const [smsBusy, setSmsBusy] = useState(false)
  const smsConfirmRef = useRef<ConfirmationResult | null>(null)
  const smsVerifierRef = useRef<RecaptchaVerifier | null>(null)
  const smsPendingE164Ref = useRef<string>('')

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.listWebauthnCredentials()
      setCredentials(res.credentials || [])
    } catch (e) {
      console.error('[PasskeySettings] list failed:', e)
      showToast({ type: 'error', message: t('passkey.listFailed') })
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleDelete = async (credentialId: string) => {
    if (deletingId) return
    if (!window.confirm(t('passkey.deleteConfirm'))) return
    setDeletingId(credentialId)
    try {
      await api.deleteWebauthnCredential(credentialId)
      // #734: 削除後は enrollment gate を再評価 (= credentials 0 になったら全 page で modal 復活)
      resetEnrollmentGate()
      showToast({ type: 'success', message: t('passkey.deleted') })
      await refresh()
    } catch (e) {
      console.error('[PasskeySettings] delete failed:', e)
      showToast({ type: 'error', message: t('passkey.deleteFailed') })
    } finally {
      setDeletingId(null)
    }
  }

  const handleResetAll = async () => {
    if (!window.confirm(t('passkey.resetConfirm'))) return
    try {
      await api.resetWebauthnCredentials()
      // #734: 全削除 → gate 再評価で必須化されたら次の page 遷移で modal が出る
      resetEnrollmentGate()
      showToast({ type: 'success', message: t('passkey.resetDone') })
      await refresh()
    } catch (e) {
      console.error('[PasskeySettings] reset failed:', e)
      showToast({ type: 'error', message: t('passkey.resetFailed') })
    }
  }

  // #815: TOTP factor の有無を反映。flag OFF では何もしない (Firebase MFA
  // 呼出ゼロ = passkey 経路 byte-for-byte 不変)。reload() は enrolledFactors
  // を最新化するため (Firebase は local cache する)。失敗時は null = 非表示。
  const totpFallbackOn = config?.totpFallbackEnabled === true
  const refreshTotp = useCallback(async () => {
    if (!totpFallbackOn) {
      setTotpEnrolled(null)
      return
    }
    try {
      await auth.currentUser?.reload()
      setTotpEnrolled(hasTotpFactor(auth.currentUser))
    } catch (e) {
      console.error('[PasskeySettings] totp status check failed:', e)
      setTotpEnrolled(null)
    }
  }, [totpFallbackOn])

  useEffect(() => {
    refreshTotp()
  }, [refreshTotp])

  // #1107: SMS 復旧 status 取得 (flag OFF は enabled=false が返り非表示)。
  const refreshSmsStatus = useCallback(async () => {
    try {
      setSmsStatus(await api.getRecoveryPhoneStatus())
    } catch (e) {
      console.error('[PasskeySettings] sms status failed:', e)
      setSmsStatus(null)
    }
  }, [])

  useEffect(() => {
    refreshSmsStatus()
    return () => {
      smsVerifierRef.current?.clear()
      smsVerifierRef.current = null
    }
  }, [refreshSmsStatus])

  // #1107: 登録フロー step 1 — Firebase linkWithPhoneNumber で SMS 送信。
  const handleSmsSendCode = async () => {
    if (smsBusy) return
    const e164 = toE164Jp(smsPhone)
    if (!e164) {
      showToast({ type: 'error', message: t('smsRecovery.phoneInvalid') })
      return
    }
    const user = auth.currentUser
    if (!user) return
    setSmsBusy(true)
    try {
      if (!smsVerifierRef.current) {
        smsVerifierRef.current = createInvisibleRecaptcha('settings-sms-recaptcha')
      }
      smsPendingE164Ref.current = e164
      smsConfirmRef.current = await startPhoneLink(user, e164, smsVerifierRef.current)
      setSmsStep('code')
      showToast({ type: 'success', message: t('smsRecovery.codeSent') })
    } catch (e) {
      const code = (e as { code?: string })?.code
      console.error('[PasskeySettings] sms link begin failed:', e)
      if (code === 'auth/provider-already-linked') {
        // 既に本人の Firebase user に link 済 (demo/prod 共有 project で他
        // env 登録済み等)。token に phone_number claim が載っているので
        // SMS を再送せず直接 backend 登録を試す。
        try {
          await registerSmsPhoneToBackend(e164)
        } finally {
          setSmsBusy(false)
        }
        return
      }
      if (code === 'auth/credential-already-in-use') {
        showToast({ type: 'error', message: t('smsRecovery.alreadyInUse') })
      } else {
        showToast({ type: 'error', message: t('smsRecovery.failed') })
      }
    } finally {
      setSmsBusy(false)
    }
  }

  // #1107: backend への登録 (trusted device + token claim 一致をサーバ検証)。
  const registerSmsPhoneToBackend = async (e164: string) => {
    await auth.currentUser?.getIdToken(true)
    try {
      await api.registerRecoveryPhone(e164, getDeviceId())
      showToast({ type: 'success', message: t('smsRecovery.registered') })
      setSmsStep('idle')
      setSmsPhone('')
      setSmsCode('')
      await refreshSmsStatus()
    } catch (err) {
      console.error('[PasskeySettings] sms register failed:', err)
      if (err instanceof ApiError && err.status === 403) {
        showToast({ type: 'error', message: t('smsRecovery.alreadyInUse') })
      } else {
        showToast({ type: 'error', message: t('smsRecovery.failed') })
      }
    }
  }

  // #1107: 登録フロー step 2 — コード検証 (link 完了) → backend 登録。
  const handleSmsConfirmCode = async () => {
    if (smsBusy || !smsConfirmRef.current) return
    setSmsBusy(true)
    try {
      await smsConfirmRef.current.confirm(smsCode.trim())
      await registerSmsPhoneToBackend(smsPendingE164Ref.current)
    } catch (e) {
      const code = (e as { code?: string })?.code
      console.error('[PasskeySettings] sms confirm failed:', e)
      if (code === 'auth/invalid-verification-code' || code === 'auth/invalid-code') {
        showToast({ type: 'error', message: t('totp.invalidCode') })
      } else if (code === 'auth/credential-already-in-use') {
        showToast({ type: 'error', message: t('smsRecovery.alreadyInUse') })
      } else {
        showToast({ type: 'error', message: t('smsRecovery.failed') })
      }
    } finally {
      setSmsBusy(false)
    }
  }

  // #1107: 解除 (Firestore は backend、Firebase Auth 側は best-effort unlink)。
  const handleSmsRemove = async () => {
    if (smsBusy) return
    if (!window.confirm(t('smsRecovery.removeConfirm'))) return
    setSmsBusy(true)
    try {
      await api.deleteRecoveryPhone(getDeviceId())
      const user = auth.currentUser
      if (user) await unlinkPhone(user)
      showToast({ type: 'success', message: t('smsRecovery.removed') })
      await refreshSmsStatus()
    } catch (e) {
      console.error('[PasskeySettings] sms remove failed:', e)
      showToast({ type: 'error', message: t('smsRecovery.failed') })
    } finally {
      setSmsBusy(false)
    }
  }

  // #1107: MFA 変更の recent-login 要求で再認証してから retry するための state。
  const [reauthResolver, setReauthResolver] = useState<MultiFactorResolver | null>(null)
  const pendingAfterReauthRef = useRef<null | (() => Promise<void>)>(null)

  // TOTP 解除の本体（再認証後の retry で再利用するため関数化）。
  const doUnenrollTotp = useCallback(async () => {
    const user = auth.currentUser
    if (!user) return
    await unenrollTotp(user)
    // required_* + passkey 0 のままなら enrollment gate が再 enroll を強制
    // (= 0 factors → TotpEnrollmentModal 復活)。passkey を別途登録済なら
    // gate は満たされたままで自然に解除のみ完了。
    resetEnrollmentGate()
    showToast({ type: 'success', message: t('totp.removed') })
    await refreshTotp()
  }, [t, refreshTotp])

  // Firebase requires-recent-login → Google 再認証してから retry。MFA(TOTP)
  // 必須で再認証自体が弾かれる場合は resolver を TotpSignInModal に渡す。
  const reauthThenRetry = useCallback(async (retry: () => Promise<void>) => {
    const user = auth.currentUser
    if (!user) return
    try {
      const r = await reauthenticateGoogle(user)
      if (r.ok) {
        await retry()
      } else {
        pendingAfterReauthRef.current = retry
        setReauthResolver(r.resolver)
      }
    } catch (e) {
      const code = (e as { code?: string })?.code
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        showToast({ type: 'info', message: t('totp.cancelled') })
      } else {
        console.error('[PasskeySettings] reauth failed:', e)
        showToast({ type: 'error', message: t('totp.removeFailed') })
      }
    }
  }, [t])

  const handleRemoveTotp = async () => {
    if (totpBusy) return
    if (!window.confirm(t('totp.removeConfirm'))) return
    setTotpBusy(true)
    try {
      await doUnenrollTotp()
    } catch (e) {
      const code = (e as { code?: string })?.code
      if (code === 'auth/requires-recent-login') {
        // セキュリティのため再認証が必要 → 再認証してから解除を retry。
        await reauthThenRetry(doUnenrollTotp)
      } else {
        console.error('[PasskeySettings] totp unenroll failed:', e)
        showToast({ type: 'error', message: t('totp.removeFailed') })
      }
    } finally {
      setTotpBusy(false)
    }
  }

  // #1114+: passkey の名前(customLabel)を任意編集。
  const [editingCredId, setEditingCredId] = useState<string | null>(null)
  const [editCredLabel, setEditCredLabel] = useState('')
  const handleRenameCredential = async (credentialId: string) => {
    const label = editCredLabel.trim()
    setEditingCredId(null)
    try {
      await api.renameWebauthnCredential(credentialId, label)
      showToast({ type: 'success', message: t('passkey.renameDone') })
      await refresh()
    } catch (e) {
      console.error('[PasskeySettings] rename failed:', e)
      showToast({ type: 'error', message: t('passkey.renameFailed') })
    }
  }

  const formatTimestamp = (ts: unknown): string => {
    if (!ts) return '-'
    try {
      // Firestore Timestamp は { _seconds, _nanoseconds } 形式 or ISO string
      let date: Date
      if (typeof ts === 'string') {
        date = new Date(ts)
      } else if (isFirestoreTimestamp(ts)) {
        date = new Date(ts._seconds * 1000)
      } else {
        return '-'
      }
      if (Number.isNaN(date.getTime())) return '-'
      // useI18n の locale を使う (= ja/en/zh/ko 等で適切な表示)
      return date.toLocaleString(locale)
    } catch {
      return '-'
    }
  }

  return (
    <div className="min-h-screen bg-warm-white safe-top pb-20">
      <Header title={t('passkey.settingsTitle')} onBack={() => navigate(-1)} />

      <div className="px-4 pt-4 space-y-4">
        {/* Browser support warning */}
        {!supported && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
            <p className="text-xs font-body text-amber-900 leading-relaxed">
              {t('passkey.unsupported')}
            </p>
          </div>
        )}

        {/* Mode indicator */}
        {!configLoading && config && (
          <p className="font-body text-xs text-text-tertiary text-center">
            {t(`passkey.modeLabel.${config.enrollmentMode}`)}
          </p>
        )}

        {/* Credentials list */}
        <div className="space-y-2">
          {loading ? (
            <p className="text-center py-8 font-body text-sm text-text-tertiary">...</p>
          ) : credentials.length === 0 ? (
            <div className="text-center py-12">
              <ShieldOff size={40} className="mx-auto text-text-tertiary mb-2" />
              <p className="font-body text-sm text-text-tertiary mb-4">{t('passkey.empty')}</p>
            </div>
          ) : (
            credentials.map(c => (
              <div key={c.credentialId} className="bg-white rounded-2xl shadow-card p-4">
                <div className="flex items-start gap-3">
                  <Fingerprint size={20} className="text-yokoso-red shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    {/* #1114+: 任意の名前(customLabel)を主表示。編集可。 */}
                    {editingCredId === c.credentialId ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          autoFocus
                          value={editCredLabel}
                          maxLength={60}
                          onChange={(e) => setEditCredLabel(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRenameCredential(c.credentialId) }}
                          placeholder={t('passkey.renamePlaceholder')}
                          className="flex-1 min-w-0 h-8 px-2 text-sm font-body border border-gray-300 rounded-lg focus:outline-none focus:border-yokoso-red"
                        />
                        <button
                          onClick={() => handleRenameCredential(c.credentialId)}
                          className="h-8 px-2 rounded-lg bg-yokoso-red text-white text-xs font-bold tap-effect"
                        >
                          {t('passkey.renameSave')}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-body text-sm font-bold text-text-primary truncate">
                          {c.customLabel || `${t('passkey.passkeyNoun')} ${c.credentialId.slice(0, 6)}`}
                        </span>
                        <button
                          onClick={() => { setEditingCredId(c.credentialId); setEditCredLabel(c.customLabel || '') }}
                          className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 tap-effect"
                          aria-label="rename passkey"
                        >
                          <Pencil size={12} className="text-text-tertiary" />
                        </button>
                        {/* #1114+ 型バッジ: 同期型 / 端末バインド型 を明示。 */}
                        {c.credentialDeviceType === 'multi_device' && (
                          <span className="text-[10px] font-display font-bold px-1.5 py-0.5 rounded-full bg-navy/10 text-navy">
                            {t('passkey.typeSynced')}
                          </span>
                        )}
                        {c.credentialDeviceType === 'single_device' && (
                          <span className="text-[10px] font-display font-bold px-1.5 py-0.5 rounded-full bg-matcha/15 text-matcha">
                            {t('passkey.typeDeviceBound')}
                          </span>
                        )}
                      </div>
                    )}
                    <code className="font-mono text-[10px] text-text-tertiary mt-0.5 block">
                      ID {c.credentialId.slice(0, 8)}
                    </code>
                    <p className="font-body text-xs text-text-tertiary mt-0.5">
                      {t('passkey.registeredAt', { date: formatTimestamp(c.createdAt) })}
                    </p>
                    {c.lastUsedAt ? (
                      <p className="font-body text-xs text-text-tertiary mt-0.5">
                        {t('passkey.lastUsedAt', { date: formatTimestamp(c.lastUsedAt) })}
                      </p>
                    ) : (
                      <p className="font-body text-xs text-text-tertiary mt-0.5">
                        {t('passkey.neverUsed')}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(c.credentialId)}
                    disabled={deletingId === c.credentialId}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 tap-effect disabled:opacity-50"
                    aria-label="delete credential"
                  >
                    <Trash2 size={14} className="text-yokoso-red" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add new credential */}
        {supported && (
          <button
            onClick={() => setEnrollOpen(true)}
            className="flex items-center justify-center gap-2 w-full h-12 rounded-xl border-2 border-dashed border-yokoso-red/40 text-yokoso-red font-display font-semibold text-sm tap-effect"
          >
            <Plus size={16} />
            {t('passkey.addNew')}
          </button>
        )}

        {/* Recovery (reset all) */}
        {credentials.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="font-body text-xs text-text-secondary leading-relaxed mb-3">
              {t('passkey.resetNote')}
            </p>
            <button
              onClick={handleResetAll}
              className="w-full h-11 rounded-xl border border-gray-200 text-sm font-body font-semibold text-yokoso-red tap-effect"
            >
              {t('passkey.resetAction')}
            </button>
          </div>
        )}

        {/* #815: 認証アプリ (TOTP) 管理 — flag ON かつ enroll 済のときだけ。
            WebAuthn 不可層 (#787) が自分の第2要素を確認/解除する導線。
            full lockout 復旧は operator 補助 (docs/infra-manual-operations.md §17)。 */}
        {totpFallbackOn && totpEnrolled === true && (
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="font-display text-sm font-bold text-text-primary mb-2">
              {t('totp.sectionTitle')}
            </p>
            <div className="bg-white rounded-2xl shadow-card p-4">
              <div className="flex items-start gap-3">
                <KeyRound size={20} className="text-yokoso-red shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-semibold text-text-primary">
                    {t('totp.enrolledStatus')}
                  </p>
                  <p className="font-body text-xs text-text-tertiary mt-1 leading-relaxed">
                    {t('totp.manageNote')}
                  </p>
                </div>
              </div>
              <button
                onClick={handleRemoveTotp}
                disabled={totpBusy}
                className="w-full h-11 mt-4 rounded-xl border border-gray-200 text-sm font-body font-semibold text-yokoso-red tap-effect disabled:opacity-50"
              >
                {t('totp.removeAction')}
              </button>
            </div>
          </div>
        )}

        {/* #1104: 能動的 TOTP 登録導線 — flag ON かつ **未 enroll** のときだけ。
            従来 TotpEnrollmentModal は「passkey 失敗後の fallback」(#838) で
            しか到達できず、同期 passkey しか持てないスマホ層が復旧 anchor
            (TOTP 再ログイン自動 trust #869c) を事前登録する入口が無かった。
            設定画面での自発的な第2要素追加は #789 (初回 gate で対等選択に
            しない) と別問題で、標準的な security 設定パターン。 */}
        {totpFallbackOn && totpEnrolled === false && (
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="font-display text-sm font-bold text-text-primary mb-2">
              {t('totp.sectionTitle')}
            </p>
            <p className="font-body text-xs text-text-secondary leading-relaxed mb-3">
              {t('totp.addNote')}
            </p>
            <button
              onClick={() => setTotpEnrollOpen(true)}
              className="flex items-center justify-center gap-2 w-full h-12 rounded-xl border-2 border-dashed border-yokoso-red/40 text-yokoso-red font-display font-semibold text-sm tap-effect"
            >
              <KeyRound size={16} />
              {t('totp.addAction')}
            </button>
          </div>
        )}

        {/* #1107: SMS 復旧用電話番号 — flag SMS_DEVICE_RECOVERY_ENABLED ON の
            ときだけ表示 (OFF = enabled:false でセクションごと非表示)。
            passkey/TOTP をやらない mass 層の復旧チャネル。番号は Firebase
            linkWithPhoneNumber の SMS 検証を経て backend (trusted device
            必須 + token claim 一致検証) に登録される。 */}
        {smsStatus?.enabled === true && (
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="font-display text-sm font-bold text-text-primary mb-2">
              {t('smsRecovery.sectionTitle')}
            </p>
            {smsStatus.registered ? (
              <div className="bg-white rounded-2xl shadow-card p-4">
                <div className="flex items-start gap-3">
                  <MessageSquare size={20} className="text-yokoso-red shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm font-semibold text-text-primary">
                      {t('smsRecovery.registeredLabel', { number: smsStatus.maskedNumber || '' })}
                    </p>
                    <p className="font-body text-xs text-text-tertiary mt-1 leading-relaxed">
                      {t('smsRecovery.sectionNote')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleSmsRemove}
                  disabled={smsBusy}
                  className="w-full h-11 mt-4 rounded-xl border border-gray-200 text-sm font-body font-semibold text-yokoso-red tap-effect disabled:opacity-50"
                >
                  {t('smsRecovery.removeAction')}
                </button>
              </div>
            ) : (
              <>
                <p className="font-body text-xs text-text-secondary leading-relaxed mb-3">
                  {t('smsRecovery.sectionNote')}
                </p>
                {smsStep === 'idle' && (
                  <button
                    onClick={() => setSmsStep('phone')}
                    className="flex items-center justify-center gap-2 w-full h-12 rounded-xl border-2 border-dashed border-yokoso-red/40 text-yokoso-red font-display font-semibold text-sm tap-effect"
                  >
                    <MessageSquare size={16} />
                    {t('smsRecovery.registerAction')}
                  </button>
                )}
                {smsStep === 'phone' && (
                  <div className="bg-white rounded-2xl shadow-card p-4">
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={smsPhone}
                      onChange={(e) => setSmsPhone(e.target.value)}
                      placeholder={t('smsRecovery.phonePlaceholder')}
                      className="w-full h-11 mb-2 px-3 text-sm font-body border border-gray-300 rounded-xl focus:outline-none focus:border-yokoso-red"
                    />
                    <button
                      onClick={handleSmsSendCode}
                      disabled={smsBusy || !smsPhone.trim()}
                      className="w-full h-11 rounded-xl font-display font-bold text-sm bg-yokoso-red text-white tap-effect disabled:opacity-50"
                    >
                      {smsBusy ? '...' : t('smsRecovery.sendCode')}
                    </button>
                  </div>
                )}
                {smsStep === 'code' && (
                  <div className="bg-white rounded-2xl shadow-card p-4">
                    <p className="font-body text-xs text-text-secondary mb-2">
                      {t('smsRecovery.codeInputLabel')}
                    </p>
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={smsCode}
                      onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="w-full h-11 mb-2 text-center tracking-[0.4em] text-lg font-mono border border-gray-300 rounded-xl focus:outline-none focus:border-yokoso-red"
                    />
                    <button
                      onClick={handleSmsConfirmCode}
                      disabled={smsBusy || smsCode.length !== 6}
                      className="w-full h-11 rounded-xl font-display font-bold text-sm bg-yokoso-red text-white tap-effect disabled:opacity-50"
                    >
                      {smsBusy ? '...' : t('smsRecovery.verifyAndRegister')}
                    </button>
                  </div>
                )}
              </>
            )}
            {/* invisible reCAPTCHA anchor (#1107 Firebase phone auth 用) */}
            <div id="settings-sms-recaptcha" />
          </div>
        )}
      </div>

      <PasskeyEnrollmentModal
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        onEnrolled={() => {
          setEnrollOpen(false)
          refresh()
        }}
      />

      {/* #1104: 設定画面からの任意 TOTP 登録 (dismissible)。 */}
      <TotpEnrollmentModal
        open={totpEnrollOpen}
        onClose={() => setTotpEnrollOpen(false)}
        onEnrolled={() => {
          setTotpEnrollOpen(false)
          refreshTotp()
        }}
      />

      {/* #1107: 再認証(recent-login 更新)に MFA が必要な場合の TOTP modal。
          resolveSignIn で再認証完了 → 保留していた操作(TOTP 解除)を retry。 */}
      {reauthResolver && (
        <TotpSignInModal
          resolver={reauthResolver}
          onResolved={async () => {
            setReauthResolver(null)
            const retry = pendingAfterReauthRef.current
            pendingAfterReauthRef.current = null
            if (retry) {
              try {
                await retry()
              } catch (e) {
                console.error('[PasskeySettings] retry after reauth failed:', e)
                showToast({ type: 'error', message: t('totp.removeFailed') })
              }
            }
          }}
          onCancel={() => {
            setReauthResolver(null)
            pendingAfterReauthRef.current = null
          }}
        />
      )}
    </div>
  )
}
